import React, { useEffect, useState } from 'react';
import { X, AlertTriangle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../../../lib/utils';
import {
  borderPrimary,
  semiBoldText,
  primaryText,
  largeTextSize,
  smallTextSize,
  secondaryText,
  mediumText,
  baseTextSize,
} from '../../../../styling/stylingHelper';
import {
  fetchSubrecipeUsage,
  deleteSubrecipe,
  SubrecipeUsage,
} from '../../../../services/subrecipes/subrecipesService';

interface DeleteSubrecipeEntityModalProps {
  subrecipeId: string;
  subrecipeName: string;
  onClose: () => void;
  onDeleted: () => void;
}

const DeleteSubrecipeEntityModal: React.FC<DeleteSubrecipeEntityModalProps> = ({
  subrecipeId,
  subrecipeName,
  onClose,
  onDeleted,
}) => {
  const { t } = useTranslation();
  const [usage, setUsage] = useState<SubrecipeUsage | null>(null);
  const [isLoadingUsage, setIsLoadingUsage] = useState(true);
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetchSubrecipeUsage(subrecipeId)
      .then((u) => {
        if (!cancelled) {
          setUsage(u);
          setIsLoadingUsage(false);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          console.error('Failed to fetch subrecipe usage:', err);
          setError(t('costPrice.subrecipes.deleteEntity.fetchUsageFailed', 'Could not check usage. Try again.'));
          setIsLoadingUsage(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [subrecipeId, t]);

  const usageCount = usage ? usage.usedByCostPrices.length + usage.usedBySubrecipes.length : 0;
  const isInUse = usageCount > 0;

  const handleConfirm = async () => {
    setIsDeleting(true);
    setError(null);
    try {
      await deleteSubrecipe(subrecipeId, isInUse);
      onDeleted();
    } catch (err) {
      console.error('Failed to delete subrecipe:', err);
      setError(t('costPrice.subrecipes.deleteEntity.failed', 'Failed to delete subrecipe.'));
      setIsDeleting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 dark:bg-black/60 flex items-center justify-center z-[60] p-4">
      <div className={cn('bg-white dark:bg-background-dark rounded-xl shadow-xl max-w-md w-full border', borderPrimary)}>
        <div className={cn('p-4 border-b flex items-center justify-between', borderPrimary)}>
          <h2 className={cn(semiBoldText, largeTextSize, primaryText)}>
            {t('costPrice.subrecipes.deleteEntity.title', 'Delete subrecipe?')}
          </h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200" disabled={isDeleting}>
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4">
          {isLoadingUsage ? (
            <p className={cn(baseTextSize, secondaryText)}>
              {t('costPrice.subrecipes.deleteEntity.checking', 'Checking usage...')}
            </p>
          ) : isInUse && usage ? (
            <>
              <div className="flex gap-2 mb-3">
                <AlertTriangle className="w-5 h-5 text-amber-500 dark:text-darkOrangeColor-dark flex-shrink-0 mt-0.5" />
                <p className={cn(baseTextSize, primaryText)}>
                  {t('costPrice.subrecipes.deleteEntity.usageWarning', {
                    name: subrecipeName,
                    count: usageCount,
                  })}
                </p>
              </div>

              {usage.usedByCostPrices.length > 0 && (
                <div className="mb-3">
                  <p className={cn(smallTextSize, mediumText, secondaryText, 'mb-1')}>
                    {t('costPrice.subrecipes.deleteEntity.costPricesHeader', 'Cost prices:')}
                  </p>
                  <ul className="list-disc list-inside space-y-0.5">
                    {usage.usedByCostPrices.map((cp) => (
                      <li key={cp.id} className={cn(smallTextSize, primaryText)}>
                        {cp.costPriceName || '(unnamed)'}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {usage.usedBySubrecipes.length > 0 && (
                <div className="mb-3">
                  <p className={cn(smallTextSize, mediumText, secondaryText, 'mb-1')}>
                    {t('costPrice.subrecipes.deleteEntity.subrecipesHeader', 'Subrecipes:')}
                  </p>
                  <ul className="list-disc list-inside space-y-0.5">
                    {usage.usedBySubrecipes.map((s) => (
                      <li key={s.id} className={cn(smallTextSize, primaryText)}>
                        {s.name}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <p className={cn(smallTextSize, secondaryText)}>
                {t(
                  'costPrice.subrecipes.deleteEntity.cascadeExplanation',
                  'Deleting will remove it from all of them. Cancel and adjust manually, or delete and remove from all?'
                )}
              </p>
            </>
          ) : (
            <p className={cn(baseTextSize, primaryText)}>
              {t('costPrice.subrecipes.deleteEntity.simpleConfirm', {
                name: subrecipeName,
                defaultValue: "Delete subrecipe '{{name}}'? This cannot be undone.",
              })}
            </p>
          )}

          {error && <p className={cn(smallTextSize, 'text-red-600 dark:text-red-400 mt-3')}>{error}</p>}
        </div>

        <div className={cn('p-4 border-t flex justify-end gap-2', borderPrimary)}>
          <button
            type="button"
            onClick={onClose}
            disabled={isDeleting}
            className="px-4 py-2 rounded-lg text-sm border border-zinc-300 dark:border-zinc-700 hover:bg-zinc-50 dark:hover:bg-zinc-800 disabled:opacity-50"
          >
            {t('common.cancel', 'Cancel')}
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={isDeleting || isLoadingUsage}
            className="px-4 py-2 rounded-lg text-sm bg-red-500 dark:bg-red-600 hover:bg-red-600 dark:hover:bg-red-700 text-white dark:text-white font-semibold disabled:opacity-50"
          >
            {isDeleting
              ? t('costPrice.subrecipes.deleteEntity.deleting', 'Deleting...')
              : isInUse
                ? t('costPrice.subrecipes.deleteEntity.confirmButton', 'Remove & Delete')
                : t('common.delete', 'Delete')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DeleteSubrecipeEntityModal;
